<script lang="ts">
  import type { ThumbnailDefinition, ThumbnailVariant } from './Thumbnail.types';

  export * from './Thumbnail.types';
</script>

<script setup lang="ts" generic="ThumbnailItem extends ThumbnailDefinition">
  import { computed, inject, toValue, useCssModule } from 'vue';

  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';
  import Image from '../Image/Image.vue';
  import { THUMBNAIL_INJECTION } from '../ThumbnailGroup/ThumbnailGroup.keys';

  const props = withDefaults(
    defineProps<{
      /**
       * Thumbnail data containing and id and the image url
       */
      thumbnail: ThumbnailItem;

      /**
       * Thumbnail alt text for the image
       * @default ''
       */
      altText?: string;

      /**
       * Whether the thumbnail has some transparency or is fully opaque
       * @default 'opaque'
       */
      variant?: ThumbnailVariant;
    }>(),
    {
      variant: 'opaque',
      altText: '',
    },
  );

  const emit = defineEmits<{
    /**
     * Emits when a thumbnail is removed
     */
    remove: [index: number];

    /**
     * Emits when a thumbnail is clicked
     */
    click: [index: number];
  }>();

  const classes = useCssModule();

  const { thumbnails, activeThumbnail, draggable, isDragging, radius, removable } = inject(
    THUMBNAIL_INJECTION.key,
    THUMBNAIL_INJECTION.defaults,
  );

  const thumbnailIndex = computed(() => toValue(thumbnails).findIndex((t) => t === props.thumbnail));

  const imageUrl = computed(() => props.thumbnail.imageUrl);

  function handleThumbnailClick() {
    activeThumbnail.value = thumbnailIndex.value;
    emit('click', thumbnailIndex.value);
  }

  function handleRemove() {
    emit('remove', thumbnailIndex.value);
    activeThumbnail.value = -1;
  }
</script>

<template>
  <li
    class="stash-thumbnail focus-visible:outline-hidden group relative cursor-pointer"
    :class="[
      {
        [classes['thumbnail-translucent']]: props.variant === 'translucent',
        [classes['thumbnail-opaque']]: props.variant === 'opaque',
        [classes['thumbnail-removable']]: removable,
        [classes['thumbnail-circle']]: radius === 'circle',
        [classes['thumbnail-rounded']]: radius === 'rounded',
      },
    ]"
    :aria-current="activeThumbnail === thumbnailIndex"
    :draggable="draggable"
    data-test="stash-thumbnail"
    tabindex="0"
    @click="handleThumbnailClick()"
  >
    <Image
      class="stash-thumbnail__img border-ice-200 outline-blue-500/15 transition-colors"
      :class="[
        classes['thumbnail-img'],
        {
          [classes['thumbnail-mask']]: removable && radius !== 'circle',
          [classes['thumbnail-active']]: activeThumbnail === thumbnailIndex && !isDragging,
          'group-hover:border-blue-500 group-focus-visible:border-blue-500 group-focus-visible:outline': !isDragging,
        },
      ]"
      data-test="stash-thumbnail|img"
      :radius="radius"
      :src="imageUrl"
      :alt="altText"
    />

    <Button
      v-if="removable"
      class="stash-thumbnail__remove"
      :class="[classes['thumbnail-remove']]"
      data-test="stash-thumbnail|remove"
      :aria-label="t('ll.thumbnail.removeButton', { index: thumbnailIndex + 1 })"
      type="button"
      icon
      @click.stop.prevent="handleRemove"
    >
      <Icon name="close" size="small" />
    </Button>

    <!-- @slot: Slot to add a hint below the image -->
    <slot name="hint" />
  </li>
</template>

<style module>
  @reference "../../../styles/main.css";

  .thumbnail-remove {
    position: absolute;
    top: calc(--spacing(1.5) * -1);
    right: calc(--spacing(1.5) * -1);
    padding: 1px;
    height: auto;
    width: auto !important;
    min-width: auto;
    border-radius: calc(infinity * 1px);
    background: var(--color-blue-500);
    color: white;

    &:hover,
    &:focus {
      background: var(--color-blue-600);
    }

    &:focus {
      outline: none;
    }
  }

  .thumbnail-img {
    border-style: solid;
    object-fit: cover;
    height: --spacing(12);
    width: --spacing(12);
    border-width: 1px;
    outline-width: --spacing(1);

    .thumbnail-translucent & {
      opacity: var(--opacity-40);

      &:hover,
      &.thumbnail-active {
        opacity: var(--opacity-100);
      }
    }
  }

  .thumbnail-active {
    border: 1px solid var(--color-ice-500);
  }

  .thumbnail-mask {
    mask-image: radial-gradient(circle at top right, transparent 20%, white 20.5%);
  }

  .thumbnail-full {
    border-radius: calc(infinity * 1px);
  }

  .thumbnail-rounded {
    border-radius: var(--radius-sm);
  }
</style>
